/**
 * Formatting utilities for dates, phone numbers, currency, etc.
 */

import { format, formatDistance, formatRelative, parseISO } from "date-fns";

/**
 * mm/dd/yyyy for date-only business fields (Business Account Pages, BD-19: organization pages only).
 * A date-only "YYYY-MM-DD" renders its written calendar date with no timezone shift (via formatDate).
 * Empty input renders "".
 */
export const formatDateMDY = (
  date: string | Date | number | null | undefined,
): string => {
  if (date === null || date === undefined || date === "") {
    return "";
  }
  return formatDate(date, "MM/dd/yyyy");
};

/**
 * Format date to readable string
 *
 * IMPORTANT: Handles timezone correctly for date-only strings (YYYY-MM-DD)
 * and full datetime strings (with time/timezone info).
 *
 * For date-only strings, we parse them as local dates to avoid timezone shift.
 * For full datetime strings, we extract just the date portion and parse as local
 * to avoid the common issue where UTC midnight appears as the previous day in
 * western timezones.
 */
export const formatDate = (
  date: string | Date | number,
  formatString: string = "MMM dd, yyyy",
): string => {
  try {
    let dateObj: Date;

    if (typeof date === "number") {
      // Unix timestamp (milliseconds)
      dateObj = new Date(date);
    } else if (typeof date === "string") {
      // DATE semantics: render the written calendar date with NO timezone math,
      // so a date-only string OR an all-day midnight-with-offset value never
      // shifts a day backward in a western browser. We take the literal Y-M-D.
      //
      // This function is intentionally DATE-only. For the TIME of an instant
      // timestamp (created_at, check-in, logs) use formatTime/formatDateTime,
      // which render the instant in the viewer's local timezone. For an EVENT's
      // SCHEDULED time use formatEventTime/formatEventDateTime (utils/eventTime),
      // which render in the event's own timezone. Splitting these three is what
      // the audit asked for after the "12:00 AM" fix — see formatters.test.ts.
      const m = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) {
        dateObj = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      } else {
        // Other formats (e.g. RFC 2822) — let date-fns try.
        dateObj = parseISO(date);
      }
    } else {
      dateObj = date;
    }

    return format(dateObj, formatString);
  } catch (error) {
    return "Invalid date";
  }
};

/**
 * Format a date range from start and end datetime strings
 * Returns "Jan 17, 2026" if same day, or "Jan 17 - Jan 18, 2026" if different days
 */
export const formatDateRange = (
  startDate: string | Date | undefined,
  endDate: string | Date | undefined,
  formatString: string = "MMM dd, yyyy",
): string => {
  if (!startDate) {
    return "No date";
  }

  const startFormatted = formatDate(startDate, formatString);

  if (!endDate) {
    return startFormatted;
  }

  // Extract just the date portions for comparison
  const getDatePart = (d: string | Date): string => {
    if (typeof d === "string") {
      if (/^\d{4}-\d{2}-\d{2}T/.test(d)) {
        return d.split("T")[0];
      }
      return d;
    }
    return format(d, "yyyy-MM-dd");
  };

  const startPart = getDatePart(startDate);
  const endPart = getDatePart(endDate);

  // Same day - just show one date
  if (startPart === endPart) {
    return startFormatted;
  }

  // Different days - show range
  const endFormatted = formatDate(endDate, formatString);

  // If same year, we can simplify (e.g., "Jan 17 - Jan 18, 2026")
  const startYear = startPart.split("-")[0];
  const endYear = endPart.split("-")[0];

  if (startYear === endYear) {
    const startWithoutYear = formatDate(startDate, "MMM dd");
    return `${startWithoutYear} - ${endFormatted}`;
  }

  return `${startFormatted} - ${endFormatted}`;
};

/**
 * Parse a value to a Date for INSTANT rendering in the viewer's local
 * timezone — for real instant timestamps (created_at, check-in times, error
 * logs, sequence evaluations, etc.). Date-only strings (YYYY-MM-DD) become a
 * local calendar date so they don't shift a day; datetimes (with or without
 * an offset) are parsed as the exact instant they denote and then formatted in
 * the browser's local zone.
 *
 * NOT for an EVENT's scheduled time — those must render in the event's own
 * timezone via formatEventTime/formatEventDateTime (utils/eventTime), not the
 * viewer's. Mixing the two is the bug the audit flagged.
 */
const toLocalInstant = (date: string | Date | number): Date => {
  if (typeof date === "number") {
    return new Date(date);
  }
  if (typeof date !== "string") {
    return date;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [y, m, d] = date.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  return parseISO(date);
};

/**
 * Format date and time of an INSTANT timestamp in the viewer's local timezone.
 */
export const formatDateTime = (date: string | Date): string => {
  try {
    return format(toLocalInstant(date), "MMM dd, yyyy hh:mm a");
  } catch {
    return "Invalid date";
  }
};

/**
 * Format a Date object to local date string
 * Use this when displaying dates that should reflect the user's local timezone
 * (e.g., "today's date" for a user in Central Time should show their local date)
 *
 * @param date - Date object (defaults to now)
 * @param options - Intl.DateTimeFormatOptions
 */
export const formatLocalDate = (
  date: Date = new Date(),
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "2-digit",
  },
): string => {
  return date.toLocaleDateString("en-US", options);
};

/**
 * Format the time of an INSTANT timestamp in the viewer's local timezone.
 * For an EVENT's scheduled time use formatEventTime (utils/eventTime) instead.
 */
export const formatTime = (date: string | Date): string => {
  try {
    return format(toLocalInstant(date), "hh:mm a");
  } catch {
    return "Invalid date";
  }
};

/**
 * Format a scheduled-send timestamp in the configured send timezone.
 * Used by Phase 2 timezone-aware displays (FlightDetail, CampaignDetail
 * flight cards, PreSendConfirmationModal) so an operator in any tz sees
 * the wall-clock time the flight will actually go out, not their browser-
 * local interpretation of the ISO string.
 *
 * Returns a string of the form "May 13, 2026 at 9:00 AM (America/Chicago)".
 * Falls back to browser-local rendering when timezone is omitted, so old
 * flights without metadata.send_timezone keep their pre-Phase-2 display.
 */
export const formatScheduledDateTime = (
  date: string | Date,
  timezone?: string | null,
): string => {
  const d = typeof date === "string" ? new Date(date) : date;
  const tz = timezone && timezone.trim() ? timezone : undefined;
  const formatted = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(d);
  return tz ? `${formatted} (${tz})` : `${formatted} (local time)`;
};

/**
 * Format date relative to now (e.g., "2 hours ago", "in 3 days")
 */
export const formatRelativeDate = (date: string | Date): string => {
  try {
    const dateObj = typeof date === "string" ? parseISO(date) : date;
    return formatDistance(dateObj, new Date(), { addSuffix: true });
  } catch (error) {
    return "Invalid date";
  }
};

/**
 * Format date relative with context (e.g., "Today at 3:00 PM", "Yesterday at 5:00 PM")
 */
export const formatRelativeDateTime = (date: string | Date): string => {
  try {
    const dateObj = typeof date === "string" ? parseISO(date) : date;
    return formatRelative(dateObj, new Date());
  } catch (error) {
    return "Invalid date";
  }
};

/**
 * Format phone number to US format (XXX) XXX-XXXX
 */
export const formatPhoneNumber = (phone: string | undefined | null): string => {
  if (!phone) {
    return "";
  }

  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, "");

  // Format based on length
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned.length === 11 && cleaned[0] === "1") {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }

  // Return original if not a valid format
  return phone;
};

/**
 * Format currency (USD by default)
 */
export const formatCurrency = (
  amount: number,
  currency: string = "USD",
  locale: string = "en-US",
): string => {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
  }).format(amount);
};

/**
 * Format number with commas
 */
export const formatNumber = (num: number, locale: string = "en-US"): string => {
  return new Intl.NumberFormat(locale).format(num);
};

/**
 * Format percentage
 */
export const formatPercentage = (
  value: number,
  decimals: number = 1,
  locale: string = "en-US",
): string => {
  return new Intl.NumberFormat(locale, {
    style: "percent",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value / 100);
};

/**
 * Format file size (bytes to human readable)
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) {
    return "0 Bytes";
  }

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Format duration in minutes to human readable
 */
export const formatDuration = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes} min${minutes !== 1 ? "s" : ""}`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (remainingMinutes === 0) {
    return `${hours} hour${hours !== 1 ? "s" : ""}`;
  }

  return `${hours} hour${hours !== 1 ? "s" : ""} ${remainingMinutes} min`;
};

/**
 * Truncate text with ellipsis
 */
export const truncate = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) {
    return text;
  }
  return text.slice(0, maxLength - 3) + "...";
};

/**
 * Capitalize first letter of each word
 */
export const titleCase = (text: string): string => {
  return text
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Convert camelCase or snake_case to Title Case
 */
export const humanize = (text: string): string => {
  return text
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

/**
 * Format address to single line
 */
export const formatAddress = (address: {
  street1?: string;
  street2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
}): string => {
  const parts = [
    address.street1,
    address.street2,
    address.city,
    address.state,
    address.postal_code,
    address.country,
  ].filter(Boolean);

  return parts.join(", ");
};

/**
 * Format full name from first and last name
 */
export const formatFullName = (firstName: string, lastName: string): string => {
  return `${firstName} ${lastName}`.trim();
};

/**
 * Get initials from name
 */
export const getInitials = (name: string): string => {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

/**
 * Format email to display name
 */
export const formatEmailName = (email: string): string => {
  const name = email.split("@")[0];
  return titleCase(name.replace(/[._-]/g, " "));
};

/**
 * Format URL to display (remove protocol and www)
 */
export const formatUrl = (url: string): string => {
  return url.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "");
};

/**
 * Format enum value to human readable
 */
export const formatEnumValue = (value: string): string => {
  return titleCase(value.replace(/_/g, " "));
};

/**
 * Format timezone offset
 */
export const formatTimezone = (timezone: string): string => {
  try {
    const date = new Date();
    return (
      date
        .toLocaleString("en-US", { timeZone: timezone, timeZoneName: "short" })
        .split(" ")
        .pop() || timezone
    );
  } catch {
    return timezone;
  }
};

/**
 * Format list to comma-separated string with "and"
 */
export const formatList = (items: string[]): string => {
  if (items.length === 0) {
    return "";
  }
  if (items.length === 1) {
    return items[0];
  }
  if (items.length === 2) {
    return `${items[0]} and ${items[1]}`;
  }

  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
};

/**
 * Format boolean to Yes/No
 */
export const formatBoolean = (value: boolean): string => {
  return value ? "Yes" : "No";
};

/**
 * Format color hex code
 */
export const formatColor = (color: string): string => {
  if (color.startsWith("#")) {
    return color.toUpperCase();
  }
  return `#${color.toUpperCase()}`;
};
